import { useEffect, useState } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

/**
 * Returns true when the signed-in user is a member of group:default/platform-lead.
 * Returns false for guest users, users without catalog entries, and while loading.
 */
export const usePlatformLeadAccess = (): boolean => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [isPlatformLead, setIsPlatformLead] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const ref = identity.userEntityRef;
        if (!ref || cancelled) return;

        const [, nsAndName] = ref.split(':');
        const [namespace, name] = nsAndName.split('/');

        const entity = await catalogApi.getEntityByRef({
          kind: 'User',
          namespace: namespace ?? 'default',
          name,
        });

        if (cancelled) return;

        const memberOf: string[] = (entity?.spec?.memberOf as string[]) ?? [];
        const isLead = memberOf.some(
          g =>
            g === 'group:default/platform-lead' ||
            g === 'platform-lead' ||
            g.endsWith('/platform-lead'),
        );
        setIsPlatformLead(isLead);
      } catch {
        // guest users or missing entities — default to no access
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [identityApi, catalogApi]);

  return isPlatformLead;
};
